import httpService from '@Utils/httpService';

const baseUrl = 'api/api-keys';

export default {
  // ============================================
  // API Key Management
  // ============================================

  /**
   * Get all API keys for the current company
   * @returns {Promise<ApiKeyVM[]>} List of API keys:
   *   - id: string - Key ID
   *   - keyPrefix: string - First 8 characters of the key (for display)
   *   - name: string - Display name for the key
   *   - permission: string - 'read' or 'write'
   *   - createdDate: string - ISO 8601 creation date
   *   - expiresDate: string|null - ISO 8601 expiration date (null = never expires)
   *   - isActive: boolean - Whether the key is active (false if revoked)
   *   - lastUsedDate: string|null - ISO 8601 last usage date
   */
  getAll() {
    return httpService.get({ url: baseUrl });
  },

  /**
   * Create a new API key
   * IMPORTANT: The rawKey is only returned once at creation time!
   * @param {Object} data - Key creation data
   * @param {string} data.name - Display name for the key (max 200 chars)
   * @param {string} data.permission - 'read' or 'write'
   * @param {string} [data.expiresDate] - Optional ISO 8601 expiration date
   * @returns {Promise<ApiKeyCreatedVM>} Created key with rawKey:
   *   - id: string - Key ID
   *   - keyPrefix: string - First 8 characters of the key
   *   - name: string - Display name
   *   - permission: string - 'read' or 'write'
   *   - createdDate: string - ISO 8601 creation date
   *   - expiresDate: string|null - ISO 8601 expiration date
   *   - isActive: boolean - true (newly created)
   *   - lastUsedDate: null - Not used yet
   *   - rawKey: string - The full API key (64 chars) - ONLY RETURNED ON CREATION
   */
  create(data) {
    return httpService.post({ url: baseUrl, data });
  },

  /**
   * Update an existing API key
   * Only permission and expiresDate can be updated (not name)
   * @param {string} id - Key ID
   * @param {Object} data - Update data
   * @param {string} [data.permission] - 'read' or 'write'
   * @param {string} [data.expiresDate] - ISO 8601 expiration date (null to remove)
   * @returns {Promise<ApiKeyVM>} Updated key
   */
  update(id, data) {
    return httpService.patch({ url: `${baseUrl}/${id}`, data });
  },

  /**
   * Revoke (soft-delete) an API key
   * Sets isActive to false - the key cannot be used after revocation
   * @param {string} id - Key ID
   * @returns {Promise<void>} 204 No Content on success
   */
  revoke(id) {
    return httpService.delete({ url: `${baseUrl}/${id}` });
  },
};
